import { noop } from 'libs/utils';
import React, { useState } from 'react';
import { Button, Cell, ConfigProvider, Field, Icon, Popup } from 'react-vant';
import { CellProps } from 'react-vant/lib/cell/PropsType';
import { cls } from 'reactutils';

export type MallCellProps = CellProps & {
  className?: string;
  value?: string;
  placeholder?: string | number;
  valuePrefix?: string;
  valueSuffix?: string;
  usePopupInput?: boolean;
  popupInputTitle?: string;
  popupInputPlaceholder?: string;
  popupInputConfirm?: Function;
  children?: React.ReactNode;
};

const MallCell = React.forwardRef(function MallCell(
  {
    className,
    value = '',
    valuePrefix = '',
    valueSuffix = '',
    placeholder,
    usePopupInput = false,
    popupInputTitle = '',
    popupInputPlaceholder = '',
    popupInputConfirm = noop,
    children,
    ...others
  }: MallCellProps,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ref: any,
) {
  const themeVars = {
    '--rv-cell-vertical-padding': '15px',
    '--rv-cell-text-color': '#666',
    '--rv-cell-value-color': '#000',
    '--rv-cell-right-icon-color': '#bbbbbb',
  };
  const [popupVisible, setPopupVisible] = useState(false);
  const [popupInputValue, setPopupInputValue] = useState(value || '');

  const handleCellClick = () => {
    if (usePopupInput) {
      setPopupInputValue(value || '');
      setPopupVisible(true);
    }
  };

  const handlePopupInputConfirm = () => {
    popupInputConfirm(popupInputValue);
    setPopupVisible(false);
  };

  const handlePopupInputChange = (val) => {
    setPopupInputValue(val);
  };

  return (
    <ConfigProvider themeVars={themeVars}>
      <div className={cls('cell', className)}>
        {value ? (
          <Cell
            value={`${valuePrefix}${value}${valueSuffix}`}
            onClick={handleCellClick}
            {...others}
          >
            {children}
          </Cell>
        ) : (
          <Cell
            className={cls(placeholder && 'placeholder')}
            value={placeholder}
            onClick={handleCellClick}
            {...others}
          >
            {children}
          </Cell>
        )}
        <Popup
          visible={popupVisible}
          position="bottom"
          onClose={() => setPopupVisible(false)}
          round
          transition="simple-bottom-transition"
        >
          <div className="popup-input-wrap">
            <Icon
              className="popup-close-icon"
              name="cross"
              onClick={() => setPopupVisible(false)}
            />
            <div className="popup-title">{popupInputTitle}</div>
            <Field
              value={popupInputValue}
              className="popup-input"
              rows={3}
              autosize
              label=""
              type="textarea"
              maxlength={200}
              placeholder={popupInputPlaceholder}
              showWordLimit
              onChange={handlePopupInputChange}
            />
            <div className="popup-confirm-btn">
              <Button type="primary" block round onClick={handlePopupInputConfirm}>
                确定
              </Button>
            </div>
          </div>
        </Popup>
        <style jsx>{`
          .cell {
            font-size: 14px;
            cursor: pointer;
          }
          .cell ::after {
            content: " ";
            display: block;
            margin: 0 16px;
            border-bottom: 1px solid #eeeeee;
          }
          .cell :global(.rv-cell__title) {
            flex: 0 0 auto;
            margin-right: 20px;
          }
          .cell :global(.rv-cell__value) {
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
          }
          .cell :global(.placeholder) :global(.rv-cell__value) {
            font-size: 14px;
            color: #bbbbbb;
          }
          .popup-input-wrap {
            position: relative;
          }
          .popup-title {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 56px;
            font-size: 17px;
            color: #333333;
            font-weight: bold;
            border-bottom: 1px solid #eeeeee;
          }
          .popup-input-wrap :global(.popup-close-icon) {
            color: #000;
            position: absolute;
            top: 16px;
            right: 16px;
          }
          .popup-input-wrap .popup-confirm-btn {
            padding: 0 16px;
            margin: 54px 0 20px;
          }
          .popup-input-wrap :global(.popup-input) {
            padding: 16px;
          }
          .popup-input-wrap :global(.rv-cell__value) {
            padding: 4px 12px;
            border: 1px solid #bbbbbb;
            border-radius: 4px;
          }
          .popup-input-wrap :global(.rv-cell)::after {
            display: none;
          }
        `}</style>
      </div>
    </ConfigProvider>
  );
});

export default MallCell;
